import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const getQCStatus = () => {
  const l = getLang(); const q = l.qualityPage || {};
  return {
    pending:  { label: q.qcPending,      color: "#06b6d4", bg: "rgba(6,182,212,0.12)"  },
    passed:   { label: q.qcPassed,         color: "#00ff9d", bg: "rgba(0,255,157,0.12)"   },
    failed:   { label: q.qcFailed,     color: "#f87171", bg: "rgba(248,113,113,0.12)" },
    partial:  { label: q.qcPartial,   color: "#60a5fa", bg: "rgba(96,165,250,0.12)"  },
    rework:   { label: q.qcRework,  color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
    scrap:    { label: q.qcScrap,         color: "#ef4444", bg: "rgba(239,68,68,0.12)"   },
  };
};

const getShipStatus = () => {
  const l = getLang(); const q = l.qualityPage || {};
  return {
    draft:     { label: q.shipDraft,        color: "#06b6d4", bg: "rgba(148,163,184,0.12)" },
    ready:     { label: q.shipReady,         color: "#06b6d4", bg: "rgba(6,182,212,0.12)"  },
    pending:   { label: q.shipPending,      color: "#06b6d4", bg: "rgba(6,182,212,0.12)"  },
    shipped:   { label: q.shipShipped,   color: "#60a5fa", bg: "rgba(96,165,250,0.12)"  },
    delivered: { label: q.shipDelivered, color: "#00ff9d", bg: "rgba(0,255,157,0.12)"   },
    cancelled: { label: q.shipCancelled,         color: "#f87171", bg: "rgba(248,113,113,0.12)" },
  };
};

const LIGHT_SHIP = {
  draft:{ color:"#64748b",bg:"#f8fafc" }, ready:{ color:"#d97706",bg:"#fffbeb" },
  pending:{ color:"#d97706",bg:"#fffbeb" }, shipped:{ color:"#2563eb",bg:"#eff6ff" },
  delivered:{ color:"#059669",bg:"#ecfdf5" }, cancelled:{ color:"#dc2626",bg:"#fef2f2" },
};

const scQC = (s, tn) => {
  if (tn === "light") {
    const L = { pending:{color:"#d97706",bg:"#fffbeb"}, passed:{color:"#059669",bg:"#ecfdf5"}, pass:{color:"#059669",bg:"#ecfdf5"}, failed:{color:"#dc2626",bg:"#fef2f2"}, fail:{color:"#dc2626",bg:"#fef2f2"}, partial:{color:"#2563eb",bg:"#eff6ff"}, rework:{color:"#7c3aed",bg:"#f5f3ff"}, scrap:{color:"#dc2626",bg:"#fef2f2"} };
    return L[s] || L.pending;
  }
  const QC_STATUS = getQCStatus();
  return QC_STATUS[s] || QC_STATUS.pending;
};
const scShip = (s, tn) => tn === "light" ? (LIGHT_SHIP[s] || LIGHT_SHIP.draft) : (getShipStatus()[s] || getShipStatus().draft);

const QCBadge = ({ status, themeName }) => {
  const QC_STATUS = getQCStatus();
  const c = scQC(status, themeName);
  const cfg = QC_STATUS[status] || QC_STATUS.pending;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:c.bg, color:c.color, border:`1px solid ${c.color}44`, letterSpacing:"0.04em", textTransform:"uppercase", whiteSpace:"nowrap" }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.color }} />{cfg.label}
    </span>
  );
};

const ShipBadge = ({ status, themeName }) => {
  const SHIP_STATUS = getShipStatus();
  const c = scShip(status, themeName);
  const cfg = SHIP_STATUS[status] || SHIP_STATUS.draft;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:c.bg, color:c.color, border:`1px solid ${c.color}44`, letterSpacing:"0.04em", textTransform:"uppercase", whiteSpace:"nowrap" }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.color }} />{cfg.label}
    </span>
  );
};

const QCDetailModal = ({ record, onClose, onCancelled, t, themeName }) => {
  const { t: l } = useLanguage();
  const q = l.qualityPage || {};
  const totalQty = record.total_qty || record.quantity_checked || 0;
  const passQty  = record.pass_qty  || record.quantity_passed  || 0;
  const failQty  = record.fail_qty  || record.quantity_failed  || 0;
  const pct = totalQty > 0 ? (passQty / totalQty * 100).toFixed(1) : 0;
  const [showCancel, setShowCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMsg, setCancelMsg] = useState(null);

  const handleCancel = async () => {
    setCancelLoading(true); setCancelMsg(null);
    try {
      const res = await fetch(`${API}/quality/check/${record.id}/cancel`, { method:"PATCH", headers:headers(), body:JSON.stringify({}) });
      const data = await res.json();
      if (res.ok) { setCancelMsg({ type:"success", text: q.qcCancelledMsg }); setTimeout(()=>onCancelled(), 1200); }
      else setCancelMsg({ type:"error", text: data.detail || (q.cancelFailed) });
    } catch { setCancelMsg({ type:"error", text: q.connectionError }); }
    setCancelLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:700, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", borderBottom:`1px solid ${t.border.default}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:t.bg.header }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:scQC(record.status,themeName).bg, border:`1px solid ${scQC(record.status,themeName).color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>🔍</div>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>KK #{record.id}  {record.product_name||record.product_code||""}</div>
              <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{q.workOrder}: {record.order_no||""} · {fmtDT(record.inspection_date||record.created_at)}</div>
            </div>
            <QCBadge status={record.status} themeName={themeName} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {record.status!=='cancelled' && <button onClick={()=>setShowCancel(true)} style={{ padding:"8px 14px", borderRadius:8, border:"1px solid rgba(248,113,113,0.4)", background:"rgba(248,113,113,0.1)", color:"#f87171", cursor:"pointer", fontSize:12, fontWeight:600 }}>🗑️ {q.cancel}</button>}
            <button onClick={onClose} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:20 }}>✕</button>
          </div>
        </div>
        {showCancel && (
          <div style={{ padding:"14px 28px", background:"rgba(248,113,113,0.08)", borderBottom:`1px solid rgba(248,113,113,0.3)` }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#f87171", marginBottom:10 }}>⚠️ {q.qcCancelConfirm}</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleCancel} disabled={cancelLoading} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:"#ef4444", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700, opacity:cancelLoading?0.6:1 }}>{cancelLoading?"...": `✓ ${q.yesCancelIt}`}</button>
              <button onClick={()=>{ setShowCancel(false); setCancelMsg(null); }} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{q.goBack}</button>
            </div>
            {cancelMsg && <div style={{ marginTop:8, fontSize:13, fontWeight:600, color:cancelMsg.type==="success"?"#10b981":"#f87171" }}>{cancelMsg.type==="success"?"✅ ":"❌ "}{cancelMsg.text}</div>}
          </div>
        )}
        <div style={{ flex:1, overflowY:"auto", padding:28, display:"flex", flexDirection:"column", gap:20 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {[
              {label: q.thisQcTotal,    value:totalQty, unit: q.pcs, color:t.accent.blue},
              {label: q.thisQcPassed,     value:passQty,  unit: q.pcs, color:t.accent.green},
              {label: q.thisQcFailed, value:failQty,  unit: q.pcs, color:t.accent.red},
              {label: q.thisQcPct,   value:pct, unit:"%", color:Number(pct)>=95?t.accent.green:Number(pct)>=80?t.accent.yellow:t.accent.red},
            ].map(({label,value,unit,color})=>(
              <div key={label} style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:12, padding:"16px 18px" }}>
                <div style={{ fontSize:11, color:t.text.muted, marginBottom:6, textTransform:"uppercase", fontWeight:600 }}>{label}</div>
                <div style={{ fontSize:22, fontWeight:800, color }}>{value}<span style={{ fontSize:12, color:t.text.muted, marginLeft:3 }}>{unit}</span></div>
              </div>
            ))}
          </div>
          <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:t.text.label, marginBottom:14, textTransform:"uppercase" }}>⚠️ {q.inspectionDetails}</div>
            {[
              [q.customer_,        record.customer],
              [q.productName,       record.product_name],
              [q.productCode,      record.product_code],
              [q.workOrder,        record.order_no],
              [q.inspector,   record.inspector_name],
              [q.inspDate, fmtDT(record.inspection_date||record.created_at)],
              [q.lotNo,         record.lot_number],
              [q.notes_,         record.notes],
            ].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${t.border.subtle}`, fontSize:13 }}>
                <span style={{ color:t.text.label }}>{k}</span><span style={{ color:t.text.primary, fontWeight:500 }}>{v||""}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ShipmentDetailModal = ({ record, onClose, onStatusChange, t, themeName }) => {
  const { t: l } = useLanguage();
  const q = l.qualityPage || {};
  const [loading, setLoading] = useState(false);
  const [shipQty, setShipQty] = useState("");
  const [error, setError] = useState("");
  const [showCancelShip, setShowCancelShip] = useState(false);
  const [cancelShipMsg, setCancelShipMsg] = useState(null);
  const [cancelShipLoading, setCancelShipLoading] = useState(false);
  const [cancelShipReason, setCancelShipReason] = useState("");

  const kkGecen = (record.remaining_quantity || 0) + (record.shipped_quantity || 0);
  const oncekiSevk = record.shipped_quantity || 0;
  const isRework = record.skip_invoice === 1 || record.source_type === 'rework';
  const kalan = isRework ? (kkGecen - oncekiSevk) : Math.min(kkGecen - oncekiSevk, (record.invoiced_quantity || 0) - (record.shipped_quantity || 0));

  const handleCancelShipment = async () => {
    setCancelShipLoading(true); setCancelShipMsg(null);
    try {
      const res = await fetch(`${API}/shipments/${record.id}/cancel`, { method:"PATCH", headers:headers(), body:JSON.stringify({ reason:cancelShipReason }) });
      const data = await res.json();
      if (res.ok) {
        setCancelShipMsg({ type:"success", text: data.stok_eklendi>0
          ? `${q.shipCancelledStock?.replace("{n}", data.stok_eklendi) || `İptal edildi. ${data.stok_eklendi} adet stoka eklendi.`}`
          : (q.shipCancelledMsg) });
        setTimeout(()=>onStatusChange(), 1200);
      } else setCancelShipMsg({ type:"error", text: data.detail || (q.cancelFailed) });
    } catch { setCancelShipMsg({ type:"error", text: q.connectionError }); }
    setCancelShipLoading(false);
  };

  const handleStatus = async () => {
    setError("");
    const qty = parseInt(shipQty)||0;
    if (qty<=0) { setError(q.errShipQtyZero); return; }
    const maxSevk = kkGecen - oncekiSevk;
    if (qty>maxSevk) { setError(`${q.errShipMaxPrefix} ${maxSevk} ${q.errShipMaxSuffix || `adet sevk edebilirsiniz (KK geçen: ${kkGecen}, daha önce sevk: ${oncekiSevk}).`}`); return; }
    setLoading(true);
    try {
      const yeniSevk = oncekiSevk + qty;
      const res = await fetch(`${API}/shipments/${record.id}/status`, { method:"PATCH", headers:headers(), body:JSON.stringify({ status:yeniSevk>=(record.quantity||kkGecen)?"shipped":"pending", shipped_quantity:yeniSevk }) });
      const json = await res.json();
      if (res.ok || json?.ok) onStatusChange(); else setError(json?.detail || json?.message || (q.errShipUpdate));
    } catch { setError(q.connectionError); }
    setLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:600, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", borderBottom:`1px solid ${t.border.default}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:t.bg.header }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:scShip(record.status,themeName).bg, border:`1px solid ${scShip(record.status,themeName).color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>🚚</div>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>{q.shipment} #{record.id}</div>
              <div style={{ fontSize:12, color:t.text.muted }}>{record.customer||record.customer_name||""}</div>
            </div>
            <ShipBadge status={record.status} themeName={themeName} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {record.status==='pending' && ['admin','manager'].includes(localStorage.getItem('role')) && (
              <button onClick={()=>setShowCancelShip(true)} style={{ padding:"8px 14px", borderRadius:8, border:"1px solid rgba(248,113,113,0.4)", background:"rgba(248,113,113,0.1)", color:"#f87171", cursor:"pointer", fontSize:12, fontWeight:600 }}>🗑️ {q.cancel}</button>
            )}
            <button onClick={onClose} style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:12 }}>✕</button>
          </div>
        </div>
        {showCancelShip && (
          <div style={{ padding:"14px 28px", background:"rgba(248,113,113,0.08)", borderBottom:`1px solid rgba(248,113,113,0.3)` }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#f87171", marginBottom:10 }}>
              ⚠️ {q.shipCancelConfirm}{oncekiSevk>0&&` ${oncekiSevk} ${q.pcs} ${q.willBeReturned}`}
            </div>
            <textarea value={cancelShipReason} onChange={e=>setCancelShipReason(e.target.value)} placeholder={q.cancelReasonPlaceholder} rows={2} style={{ width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid rgba(248,113,113,0.4)`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", resize:"none", marginBottom:10, boxSizing:"border-box" }} />
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleCancelShipment} disabled={cancelShipLoading} style={{ padding:"8px 16px", borderRadius:8, border:"none", background:"#ef4444", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700, opacity:cancelShipLoading?0.6:1 }}>{cancelShipLoading?"...": `✓ ${q.yesCancelIt}`}</button>
              <button onClick={()=>{ setShowCancelShip(false); setCancelShipMsg(null); setCancelShipReason(""); }} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{q.goBack}</button>
            </div>
            {cancelShipMsg && <div style={{ marginTop:8, fontSize:13, fontWeight:600, color:cancelShipMsg.type==="success"?"#10b981":"#f87171" }}>{cancelShipMsg.type==="success"?"✅ ":"❌ "}{cancelShipMsg.text}</div>}
          </div>
        )}
        <div style={{ flex:1, overflowY:"auto", padding:28, display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
            {[
              [q.customer_,               record.customer||record.customer_name],
              [q.product_,                  record.product_name||record.product_code],
              [q.productCode,             record.product_code],
              [q.orderNo,            record.order_no],
              [q.totalQty,         `${record.quantity || kkGecen} ${q.pcs}`],
              [q.shippedOf,           `${oncekiSevk} / ${record.quantity||kkGecen} ${q.pcs} (%${(record.quantity||kkGecen)>0?Math.round(oncekiSevk/(record.quantity||kkGecen)*100):0})`],
              [q.remainingToShip,   `${(record.quantity||kkGecen) - oncekiSevk} ${q.pcs} (${kkGecen - oncekiSevk} ${q.readyToShip})`],
              [q.shipDate,           fmt(record.ship_date)],
              [q.notes_,                record.notes],
            ].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${t.border.subtle}`, fontSize:13 }}>
                <span style={{ color:t.text.label }}>{k}</span><span style={{ color:t.text.primary, fontWeight:500 }}>{v||""}</span>
              </div>
            ))}
          </div>
          {kalan > 0 && (
            <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
              <div style={{ fontSize:14, fontWeight:600, color:t.text.primary, marginBottom:12 }}>🚚 {q.doShipment}</div>
              {(!record?.skip_invoice && record?.source_type !== 'rework') && ((record?.invoiced_quantity || 0) - (record?.shipped_quantity || 0)) <= 0 ? (
                <div style={{ padding:"10px 14px", background:"#f9731611", border:"1px solid #f9731633", borderRadius:8, fontSize:12, color:"#f97316" }}>
                  ⚠️ {q.invoiceRequired}
                </div>
              ) : (
                <>
                  <div style={{ fontSize:12, color:t.text.muted, marginBottom:8 }}>{q.shipQtyLabel?.replace("{n}", kalan) || `Sevk edilecek miktar (max: ${kalan} ${q.pcs})`}</div>
                  <div style={{ display:"flex", gap:10 }}>
                    <input type="number" min={1} max={kalan} value={shipQty} onChange={e=>setShipQty(e.target.value)} placeholder={`1 - ${kalan}`} style={{ flex:1, padding:"10px 14px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.input, color:t.text.primary, fontSize:14 }} />
                    <button onClick={handleStatus} disabled={loading} style={{ padding:"10px 20px", borderRadius:8, border:"none", background:"#10b981", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700, opacity:loading?0.6:1 }}>{loading?"...": `🚚 ${q.shipNow}`}</button>
                  </div>
                  {error && <div style={{ marginTop:8, fontSize:12, color:"#ef4444" }}>{error}</div>}
                </>
              )}
            </div>
          )}
          {(record.shipped_quantity||0) >= (record.quantity||kkGecen) && <div style={{ textAlign:"center", padding:20, color:"#10b981", fontWeight:600, fontSize:14 }}>✅ {q.allShipped}</div>}
        </div>
      </div>
    </div>
  );
};

const QCFormModal = ({ onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const q = l.qualityPage || {};
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [prevPassed, setPrevPassed] = useState(0);
  const [remainingQty, setRemainingQty] = useState(0);
  const [form, setForm] = useState({ work_order_no:"", product_code:"", product_name:"", customer:"", total_qty:"", pass_qty:"", fail_qty:"", inspector_name:localStorage.getItem("username")||"", inspection_type:"final", lot_number:"", inspection_date:new Date().toISOString().slice(0,10), notes:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bomItems, setBomItems] = useState([]);
  const [bomDecisions, setBomDecisions] = useState({});
  const [bomLoading, setBomLoading] = useState(false);

  useEffect(()=>{ 
    const fetchWO = () => fetch(`${API}/work-orders`,{headers:headers()})
.then(r=>r.ok?r.json():[])
.then(d=>{ const list=Array.isArray(d)?d:[]; setWorkOrders(
  list
    .filter(w => ["qc_pending","final_qc_pending"].includes(w.status))
    .map(w => {
      const isMainWO = !w.parent_order_no;
      if (isMainWO && w.status === "qc_pending") {
        return { ...w, status: "final_qc_pending", work_order_status: "final_qc_pending" };
      }
      return w;
    })
); }).catch(()=>{});
    fetchWO();
    const interval = setInterval(fetchWO, 10000);
    return () => clearInterval(interval);
  },[]);

  const handleSelectWO = async (wo) => {
    if (!wo) { setSelectedWO(null); return; }
    const isSubOrder = !!(wo.parent_order_no);
    const isQcPending = wo.status === "qc_pending";
    const defaultType = (isSubOrder && !isQcPending) ? "inprocess" : "final";
    setForm(p=>({ ...p, work_order_no:wo.order_no, product_code:wo.product_code||"", product_name:wo.product_name||wo.product_code||"", customer:wo.customer||"", total_qty:"", pass_qty:"", fail_qty:"", inspection_type: defaultType }));
    try {
      const res = await fetch(`${API}/quality/checks`,{headers:headers()});
      const d = res.ok?await res.json():{quality_checks:[]};
      const checks=(d.quality_checks||[]).filter(c=>c.order_no===wo.order_no);
      const totalPrev=checks.reduce((s,c)=>s+(c.quantity_passed||0),0);
      let salesQty=wo.quantity||0;
      if (wo.sales_order_no) {
        try {
          const soRes=await fetch(`${API}/sales-orders`,{headers:headers()});
          if (soRes.ok) {
            const soData=await soRes.json();
            const so=(Array.isArray(soData)?soData:[]).find(s=>s.order_no===wo.sales_order_no);
            if (so) { const dr=await fetch(`${API}/sales-orders/${so.id}`,{headers:headers()}); if (dr.ok) { const detail=await dr.json(); const soItem=(detail.items||[]).find(i=>i.product_code===wo.product_code); if (soItem) salesQty=soItem.quantity; } }
          }
        } catch {}
      }
      const isQcPending = wo.status === 'qc_pending';
      const effectivePrev = isQcPending ? 0 : totalPrev;
      const finalQty = wo.quantity || 0;  // iş emri miktarı (KK için)
      const displayQty = salesQty || wo.quantity || 0;  // sipariş miktarı (gösterim için)
      setPrevPassed(effectivePrev); setRemainingQty(Math.max(0,finalQty-effectivePrev)); setSelectedWO({...wo,quantity:finalQty,sales_order_qty:displayQty});
    } catch {}
  };

  const handleTotalChange=(val)=>{ const v=Math.max(0,parseInt(val)||0); const fail=Math.min(parseInt(form.fail_qty)||0,v); setForm(p=>({...p,total_qty:String(v),pass_qty:String(Math.max(0,v-fail)),fail_qty:String(fail)})); };
  const handleFailChange=async(val)=>{ 
    const fail=Math.max(0,parseInt(val)||0); 
    const total=parseInt(form.total_qty)||0; 
    setForm(p=>({...p,fail_qty:String(fail),pass_qty:String(Math.max(0,total-fail))}));
    if(fail>0 && form.product_code) {
      setBomLoading(true);
      try {
        const res = await fetch(`${API}/bom/${form.product_code}/tree`,{headers:headers()});
        if(res.ok) {
          const d = await res.json();
          const items = d.tree || [];
          setBomItems(items);
          const decisions = {};
          items.forEach(item => {
            const qty = Math.ceil(item.quantity_required * fail);
            const hasStock = item.stock_qty >= qty;
            const isExternal = item.material_type === 'purchased';
            decisions[item.id] = { checked: true, qty: qty, error_type: 'dimensional', decision: hasStock ? 'stock' : isExternal ? 'purchase' : 're_manufacture' };
          });
          setBomDecisions(decisions);
        }
      } catch(e) { console.error(e); }
      setBomLoading(false);
    } else {
      setBomItems([]);
      setBomDecisions({});
    }
  };

  const handleSave = async () => {
    setError("");
    const totalQty=parseInt(form.total_qty)||0; const passQty=parseInt(form.pass_qty)||0; const failQty=parseInt(form.fail_qty)||0;
    if (!form.work_order_no){setError(q.errSelectWO);return;}
    if (totalQty<=0){setError(q.errQtyZero);return;}
    if (passQty+failQty>totalQty){setError(q.errQtyExceeds);return;}
    if (selectedWO&&remainingQty===0){setError(q.err100Pct);return;}
    if (selectedWO&&totalQty>remainingQty){setError(`${q.errRemainingQty}: ${remainingQty} ${selectedWO.unit||q.pcs}`);return;}
    setLoading(true);
    try {
      const wo=workOrders.find(w=>w.order_no===form.work_order_no);
      const failedComponents = bomItems
        .filter(item => bomDecisions[item.id]?.checked)
        .map(item => ({
          material_code: item.material_code,
          material_name: item.material_name,
          quantity: bomDecisions[item.id]?.qty || 0,
          error_type: bomDecisions[item.id]?.error_type || 'other',
          decision: bomDecisions[item.id]?.decision || 're_manufacture',
          stock_qty: item.stock_qty,
          material_type: item.material_type
        }));
      const body={work_order_id:wo?.id||0,order_no:form.work_order_no,product_code:form.product_code,quantity_checked:totalQty,quantity_passed:passQty,quantity_failed:failQty,checked_by:form.inspector_name||localStorage.getItem("username"),check_type:(form.inspection_type==="inprocess"?"Proses İçi":"Final Kontrol"),failed_components:failedComponents,notes:[form.inspection_type?`${q.inspType}: ${form.inspection_type}`:"",form.lot_number?`Lot: ${form.lot_number}`:"",form.notes].filter(Boolean).join(" | ")};
      const res=await fetch(`${API}/quality/check`,{method:"POST",headers:headers(),body:JSON.stringify(body)});
      if (!res.ok) throw new Error((await res.json()).detail||(q.errSave));
      if (failedComponents.length > 0 && failQty > 0) {
        await fetch(`${API}/quality/process-failed-components`, { method: 'POST', headers: headers(), body: JSON.stringify({ order_no: form.work_order_no, customer: form.customer, failed_components: failedComponents }) });
      }
      onSaved();
    } catch(e){setError(e.message);}
    setLoading(false);
  };

  const IS={width:"100%",padding:"10px 12px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:8,color:t.text.primary,fontSize:13,outline:"none",boxSizing:"border-box"};
  const LS={display:"block",fontSize:11,fontWeight:700,color:t.text.label,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6};

  return (
    <div style={{position:"fixed",inset:0,zIndex:1100,background:t.bg.overlay,backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:t.bg.modal,border:`1px solid ${t.border.default}`,borderRadius:20,width:"100%",maxWidth:660,maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:t.shadow.modal}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"20px 28px",borderBottom:`1px solid ${t.border.default}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:t.bg.header}}>
          <div style={{fontSize:17,fontWeight:700,color:t.text.primary}}>🔍 {q.newQCRecord}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:t.text.muted,cursor:"pointer",fontSize:20}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:28}}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <label style={LS}>{q.workOrderSelect} * ({q.onlyInProduction})</label>
              <select value={form.work_order_no} onChange={e=>{const wo=workOrders.find(w=>w.order_no===e.target.value);handleSelectWO(wo);}} style={IS}>
                <option value="">-- {q.selectWO} --</option>
                {workOrders.map(w=>(<option key={w.id} value={w.order_no}>{w.order_no} {w.product_code} ({w.customer||""})</option>))}
              </select>
              {workOrders.length===0&&<div style={{marginTop:8,fontSize:12,color:t.accent.yellow}}>⚠️ {q.noWOInProduction}</div>}
            </div>
            {selectedWO&&(
              <div style={{padding:"12px 16px",background:t.bg.metricCard,border:`1px solid ${t.border.default}`,borderRadius:12}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10}}>
                  {[
                    [q.customer_, selectedWO.customer||"", t.text.primary,13],
                    [q.orderQty, `${selectedWO.sales_order_qty||selectedWO.quantity} ${selectedWO.unit||q.pcs}`, t.accent.blue,14],
                    [q.prevPassed, `${prevPassed} ${selectedWO.unit||q.pcs}`, t.accent.green,14],
                    [q.remainingQC, `${remainingQty} ${selectedWO.unit||q.pcs}`, remainingQty>0?t.accent.yellow:t.accent.red,14],
                  ].map(([label,val,col,fs])=>(
                    <div key={label}><div style={{fontSize:10,color:t.text.muted,textTransform:"uppercase",marginBottom:3}}>{label}</div><div style={{fontSize:fs,fontWeight:800,color:col}}>{val}</div></div>
                  ))}
                </div>
                {selectedWO.quantity>0&&(
                  <div style={{marginTop:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:11,color:t.text.muted}}>{q.woPassRate}</span><span style={{fontSize:12,fontWeight:700,color:t.accent.green}}>{((prevPassed/selectedWO.quantity)*100).toFixed(1)}% ({prevPassed}/{selectedWO.quantity})</span></div>
                    <div style={{height:6,background:t.bg.tag,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min((prevPassed/selectedWO.quantity)*100,100)}%`,background:t.accent.green,borderRadius:3}}/></div>
                  </div>
                )}
                {remainingQty===0&&<div style={{marginTop:10,padding:"6px 10px",background:t.accent.greenBg,borderRadius:8,fontSize:12,color:t.accent.green,fontWeight:600}}>✅ {q.wo100Pct}</div>}
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
              <div><label style={LS}>{q.productName}</label><input value={form.product_name} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
              <div><label style={LS}>{q.productCode}</label><input value={form.product_code} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
              <div><label style={LS}>{q.customer_}</label><input value={form.customer} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
              <div><label style={LS}>{q.checkedQty} *{selectedWO&&remainingQty>0&&<span style={{color:t.accent.blue,fontSize:10,marginLeft:4}}>({q.max}: {remainingQty})</span>}</label><input type="number" value={form.total_qty} onChange={e=>handleTotalChange(e.target.value)} min="1" max={remainingQty||undefined} style={IS} placeholder="0"/></div>
              <div><label style={LS}>{q.failQty}</label><input type="number" value={form.fail_qty} onChange={e=>handleFailChange(e.target.value)} min="0" max={form.total_qty||undefined} style={IS} placeholder="0"/></div>
              <div><label style={LS}>{q.passQtyAuto}</label><input type="number" value={form.pass_qty} readOnly style={{...IS,background:t.bg.metricCard,color:t.accent.green,fontWeight:700}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div><label style={LS}>{q.inspector}</label><input value={form.inspector_name} onChange={e=>setForm(p=>({...p,inspector_name:e.target.value}))} style={IS}/></div>
              <div><label style={LS}>{q.inspType}</label>
                <select value={form.inspection_type} onChange={e=>setForm(p=>({...p,inspection_type:e.target.value}))} style={IS}>
                  {(
                    (selectedWO?.status || selectedWO?.work_order_status) === "final_qc_pending" ||
                    ((selectedWO?.status || selectedWO?.work_order_status) === "qc_pending" && !selectedWO?.parent_order_no)
                  ) && <option value="final">{q.inspFinal}</option>}
                  {!(
                    (selectedWO?.status || selectedWO?.work_order_status) === "final_qc_pending" ||
                    ((selectedWO?.status || selectedWO?.work_order_status) === "qc_pending" && !selectedWO?.parent_order_no)
                  ) && <option value="inprocess">{q.inspInProcess}</option>}
                  <option value="incoming">{q.inspIncoming}</option>
                  <option value="outgoing">{q.inspOutgoing}</option>
                </select>
              </div>
              <div><label style={LS}>{q.lotNo}</label><input value={form.lot_number} onChange={e=>setForm(p=>({...p,lot_number:e.target.value}))} style={IS} placeholder="LOT-001"/></div>
              <div><label style={LS}>{q.inspDate}</label><input type="date" value={form.inspection_date} onChange={e=>setForm(p=>({...p,inspection_date:e.target.value}))} style={IS}/></div>
            </div>
            {bomLoading && <div style={{padding:"12px",textAlign:"center",color:t.text.muted,fontSize:13}}>⏳ BOM yükleniyor...</div>}
            {bomItems.length>0 && parseInt(form.fail_qty)>0 && (
              <div style={{border:`1px solid ${t.border.default}`,borderRadius:12,overflow:"hidden"}}>
                <div style={{padding:"10px 16px",background:t.bg.header,borderBottom:`1px solid ${t.border.default}`,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,fontWeight:700,color:t.accent.red}}>{q.defectiveComponents}</span>
                  <span style={{fontSize:11,color:t.text.muted}}>({parseInt(form.fail_qty)} adet red için BOM analizi)</span>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:t.bg.metricCard}}>
                        {["",q.component,q.stock,q.pieceCount,q.defectType,q.decision].map(h=>(
                          <th key={h} style={{padding:"8px 10px",textAlign:"left",color:t.text.muted,fontWeight:600,fontSize:11,borderBottom:`1px solid ${t.border.default}`}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bomItems.map(item=>{
                        const dec = bomDecisions[item.id] || {};
                        const needQty = Math.ceil(item.quantity_required * parseInt(form.fail_qty||0));
                        const hasStock = item.stock_qty >= needQty;
                        return (
                          <tr key={item.id} style={{borderBottom:`1px solid ${t.border.default}33`,opacity:dec.checked===false?0.4:1}}>
                            <td style={{padding:"8px 10px"}}><input type="checkbox" checked={dec.checked!==false} onChange={e=>setBomDecisions(p=>({...p,[item.id]:{...p[item.id],checked:e.target.checked}}))} /></td>
                            <td style={{padding:"8px 10px"}}><div style={{fontWeight:600,color:t.text.primary}}>{item.material_name}</div><div style={{fontSize:10,color:t.text.muted}}>{item.material_code}</div></td>
                            <td style={{padding:"8px 10px"}}><span style={{color:hasStock?t.accent.green:t.accent.red,fontWeight:600}}>{item.stock_qty} {item.unit}</span></td>
                            <td style={{padding:"8px 10px"}}><input type="number" min="0" value={dec.qty||needQty} onChange={e=>setBomDecisions(p=>({...p,[item.id]:{...p[item.id],qty:parseInt(e.target.value)||0}}))} style={{width:60,padding:"4px 6px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:6,color:t.text.primary,fontSize:12}} /></td>
                            <td style={{padding:"8px 10px"}}>
                              <select value={dec.error_type||'dimensional'} onChange={e=>setBomDecisions(p=>({...p,[item.id]:{...p[item.id],error_type:e.target.value}}))} style={{padding:"4px 6px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:6,color:t.text.primary,fontSize:11}}>
                                <option value="dimensional">{q.defectDimensional}</option><option value="visual">{q.defectVisual}</option><option value="functional">{q.defectFunctional}</option><option value="material">{q.defectMaterial}</option><option value="other">{q.defectOther}</option>
                              </select>
                            </td>
                            <td style={{padding:"8px 10px"}}>
                              <select value={dec.decision||'re_manufacture'} onChange={e=>setBomDecisions(p=>({...p,[item.id]:{...p[item.id],decision:e.target.value}}))} style={{padding:"4px 6px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:6,color:t.text.primary,fontSize:11,fontWeight:600}}>
                                <option value="re_manufacture">{q.dispositionRemanufacture}</option><option value="stock">{q.dispositionStock}</option><option value="purchase">{q.dispositionPurchase}</option><option value="scrap">{q.dispositionScrap}</option><option value="use_as_is">{q.dispositionUseAsIs}</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{padding:"10px 16px",background:t.bg.metricCard,borderTop:`1px solid ${t.border.default}`,display:"flex",gap:16,fontSize:11,color:t.text.muted}}>
                  {[[q.dispositionRemanufacture,'re_manufacture',t.accent.blue],[q.dispositionStock,'stock',t.accent.green],[q.dispositionPurchase,'purchase',t.accent.yellow],[q.dispositionScrap,'scrap',t.accent.red]].map(([label,key,color])=>{
                    const count=Object.values(bomDecisions).filter(d=>d.checked!==false&&d.decision===key).length;
                    return count>0&&(<span key={label} style={{color,fontWeight:700}}>{label}: {count}</span>);
                  })}
                </div>
              </div>
            )}
            <div><label style={LS}>{q.notes_}</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} style={{...IS,resize:"vertical"}}/></div>
            {error&&<div style={{padding:"10px 16px",background:t.accent.redBg,border:`1px solid ${t.accent.red}44`,borderRadius:8,color:t.accent.red,fontSize:13}}>❌ {error}</div>}
          </div>
        </div>
        <div style={{padding:"16px 28px",borderTop:`1px solid ${t.border.default}`,display:"flex",justifyContent:"flex-end",gap:12,background:t.bg.header}}>
          <button onClick={onClose} style={{padding:"10px 20px",borderRadius:8,border:`1px solid ${t.border.input}`,background:"none",color:t.text.secondary,cursor:"pointer",fontSize:13}}>{q.cancel}</button>
          <button onClick={handleSave} disabled={loading||(selectedWO&&remainingQty===0)} style={{padding:"10px 24px",borderRadius:8,border:"none",background:loading||(selectedWO&&remainingQty===0)?t.bg.tag:"linear-gradient(135deg,#3b82f6,#6366f1)",color:"#fff",cursor:loading||(selectedWO&&remainingQty===0)?"default":"pointer",fontSize:13,fontWeight:600}}>
            {loading?(q.saving):(q.save)}
          </button>
        </div>
      </div>
    </div>
  );
};

const ShipFormModal = ({ data, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const q = l.qualityPage || {};
  const isEdit=!!data;
  const [pendingShipments,setPendingShipments]=useState([]);
  const [selectedShipment,setSelectedShipment]=useState(null);
  const [maxQty,setMaxQty]=useState(0);
  const [form,setForm]=useState({customer_name:data?.customer||data?.customer_name||"",product_name:data?.product_name||"",product_code:data?.product_code||"",quantity:data?.quantity||"",ship_quantity:"",unit:data?.unit||"adet",ship_date:new Date().toISOString().slice(0,10),carrier:data?.carrier||"",order_no:data?.order_no||"",notes:data?.notes||""});
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  useEffect(()=>{fetch(`${API}/shipments`,{headers:headers()}).then(r=>r.ok?r.json():[]).then(d=>{const list=Array.isArray(d)?d:d.items||[];setPendingShipments(list.filter(s=>s.status==="pending"||s.status==="ready"));}).catch(()=>{});},[]);

  const handleSelectShipment=(shipment)=>{
    if(!shipment){setSelectedShipment(null);setMaxQty(0);return;}
    setSelectedShipment(shipment);
    const available=(shipment.quantity||0)-(shipment.shipped_quantity||0);
    setMaxQty(available);
    setForm(p=>({...p,customer_name:shipment.customer||shipment.customer_name||"",product_name:shipment.product_name||shipment.product_code||"",product_code:shipment.product_code||"",quantity:shipment.quantity||"",ship_quantity:String(available),unit:shipment.unit||"adet",order_no:shipment.order_no||""}));
  };

  const updateShipmentStatus=async(nextStatus)=>{
    setError("");
    const shipQty=parseInt(form.ship_quantity)||0;
    const target=selectedShipment||data;
    if(!target){setError(q.errSelectShipment);return;}

    const currentShipped=parseInt(target.shipped_quantity||0);
    const totalQty=parseInt(target.quantity||form.quantity||0);
    const nextShipped=nextStatus==="delivered" ? totalQty : currentShipped + shipQty;

    if(nextStatus!=="delivered" && shipQty<=0){setError(q.errShipQtyZero);return;}
    if(nextStatus!=="delivered" && !isEdit && shipQty>maxQty){setError(`${q.errMaxShip}: ${maxQty} ${form.unit}`);return;}
    if(nextStatus==="delivered" && currentShipped < totalQty && target.status!=="shipped"){
      setError(q.errFullShipmentBeforeDelivered);
      return;
    }

    setLoading(true);
    try{
      const res=await fetch(`${API}/shipments/${target.id}/status`,{
        method:"PATCH",
        headers:headers(),
        body:JSON.stringify({
          status:nextStatus,
          shipped_quantity:nextShipped,
          carrier:form.carrier,
          ship_date:form.ship_date,
          notes:form.notes,
          shipped_by:localStorage.getItem("username")||"admin"
        })
      });
      if(!res.ok)throw new Error((await res.json()).detail||(q.errSave));
      onSaved();
    }catch(e){setError(e.message);}
    setLoading(false);
  };

  const handleSave=async()=>{
    const shipQty=parseInt(form.ship_quantity)||0;
    const nextStatus=shipQty>=maxQty?"shipped":"pending";
    await updateShipmentStatus(nextStatus);
  };

  const IS={width:"100%",padding:"10px 12px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:8,color:t.text.primary,fontSize:13,outline:"none",boxSizing:"border-box"};
  const LS={display:"block",fontSize:11,fontWeight:700,color:t.text.label,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6};

  return(
    <div style={{position:"fixed",inset:0,zIndex:1100,background:t.bg.overlay,backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:t.bg.modal,border:`1px solid ${t.border.default}`,borderRadius:20,width:"100%",maxWidth:620,maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:t.shadow.modal}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"20px 28px",borderBottom:`1px solid ${t.border.default}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:t.bg.header}}>
          <div style={{fontSize:17,fontWeight:700,color:t.text.primary}}>{isEdit?`✏️ ${q.editShipment}`:`🚚 ${q.doShipment}`}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:t.text.muted,cursor:"pointer",fontSize:20}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:28}}>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {!isEdit&&(
              <div>
                <label style={LS}>{q.selectPendingShipment} *</label>
                <select onChange={e=>{const s=pendingShipments.find(p=>String(p.id)===e.target.value);handleSelectShipment(s||null);}} style={IS}>
                  <option value="">-- {q.selectShipment} --</option>
                  {pendingShipments.map(s=>(<option key={s.id} value={s.id}>{s.order_no}  {s.customer_name||s.customer||q.noCustomer}  {q.pending_}: {(s.quantity||0)-(s.shipped_quantity||0)} {s.unit||q.pcs}</option>))}
                </select>
                {pendingShipments.length===0&&<div style={{marginTop:8,fontSize:12,color:t.accent.yellow}}>⚠️ {q.noPendingShipments}</div>}
              </div>
            )}
            {selectedShipment&&(
              <div style={{padding:"12px 16px",background:t.bg.metricCard,border:`1px solid ${t.border.default}`,borderRadius:12}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div><div style={{fontSize:10,color:t.text.muted,textTransform:"uppercase",marginBottom:3}}>{q.totalOrderLabel}</div><div style={{fontSize:14,fontWeight:800,color:t.accent.blue}}>{selectedShipment.quantity} {selectedShipment.unit||q.pcs}</div></div>
                  <div><div style={{fontSize:10,color:t.text.muted,textTransform:"uppercase",marginBottom:3}}>{q.shippable}</div><div style={{fontSize:14,fontWeight:800,color:t.accent.green}}>{maxQty} {selectedShipment.unit||q.pcs}</div></div>
                </div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{gridColumn:"1/-1"}}><label style={LS}>{q.customer_}</label><input value={form.customer_name} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
              <div><label style={LS}>{q.product_}</label><input value={form.product_name} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
              <div><label style={LS}>{q.orderNo}</label><input value={form.order_no} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div><label style={LS}>{q.thisShipQty} *{maxQty>0&&<span style={{color:t.accent.blue,fontSize:10,marginLeft:4}}>({q.max}: {maxQty})</span>}</label><input type="number" value={form.ship_quantity} onChange={e=>setForm(p=>({...p,ship_quantity:String(Math.max(0,Math.min(parseInt(e.target.value)||0,maxQty)))}))} min="1" max={maxQty||undefined} style={{...IS,borderColor:t.accent.blue}}/></div>
              <div><label style={LS}>{q.unit_}</label><input value={form.unit} readOnly style={{...IS,background:t.bg.metricCard,color:t.text.muted}}/></div>
              <div><label style={LS}>{q.shipDate}</label><input type="date" value={form.ship_date} onChange={e=>setForm(p=>({...p,ship_date:e.target.value}))} style={IS}/></div>
              <div><label style={LS}>{q.carrier}</label><input value={form.carrier} onChange={e=>setForm(p=>({...p,carrier:e.target.value}))} style={IS}/></div>
            </div>
            {maxQty>0&&form.ship_quantity&&parseInt(form.ship_quantity)<maxQty&&<div style={{padding:"10px 14px",background:t.accent.yellowBg,border:`1px solid ${t.accent.yellow}44`,borderRadius:8,fontSize:13,color:t.accent.yellow}}>⚠️ {q.partialShipNote?.replace("{qty}",form.ship_quantity).replace("{max}",maxQty).replace("{unit}",form.unit).replace("{remaining}",maxQty-parseInt(form.ship_quantity)) || `Kısmi Sevkiyat: ${form.ship_quantity} / ${maxQty} ${form.unit}. Kalan: ${maxQty-parseInt(form.ship_quantity)} ${form.unit} bekleyecek.`}</div>}
            {maxQty>0&&form.ship_quantity&&parseInt(form.ship_quantity)>=maxQty&&<div style={{padding:"10px 14px",background:t.accent.greenBg,border:`1px solid ${t.accent.green}44`,borderRadius:8,fontSize:13,color:t.accent.green}}>✅ {q.allPendingWillShip}</div>}
            <div><label style={LS}>{q.notes_}</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} style={{...IS,resize:"vertical"}}/></div>
            {error&&<div style={{padding:"10px 16px",background:t.accent.redBg,border:`1px solid ${t.accent.red}44`,borderRadius:8,color:t.accent.red,fontSize:13}}>❌ {error}</div>}
          </div>
        </div>
        <div style={{padding:"16px 28px",borderTop:`1px solid ${t.border.default}`,display:"flex",flexDirection:"column",gap:8,background:t.bg.header}}>
          {selectedShipment && !selectedShipment.skip_invoice && selectedShipment.source_type !== 'rework' && (selectedShipment.invoiced_quantity || 0) <= (selectedShipment.shipped_quantity || 0) && (
            <div style={{ padding: "8px 12px", background: "#f9731611", border: "1px solid #f9731633", borderRadius: 8, fontSize: 12, color: "#f97316" }}>
              ⚠️ {q.invoiceRequiredShip}
            </div>
          )}
          <div style={{display:"flex",justifyContent:"flex-end",gap:12}}>
            <button onClick={onClose} style={{padding:"10px 20px",borderRadius:8,border:`1px solid ${t.border.input}`,background:"none",color:t.text.secondary,cursor:"pointer",fontSize:13}}>{q.cancel}</button>
            <button onClick={handleSave} disabled={loading || (!selectedShipment && !isEdit) || (selectedShipment && !selectedShipment.skip_invoice && selectedShipment.source_type !== 'rework' && selectedShipment.invoice_status !== 'invoiced')} style={{padding:"10px 24px",borderRadius:8,border:"none",background:(loading || (!selectedShipment && !isEdit) || (selectedShipment && selectedShipment.invoice_status !== 'invoiced')) ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)",color:"#fff",cursor:(loading || (!selectedShipment && !isEdit) || (selectedShipment && selectedShipment.invoice_status !== 'invoiced')) ? "default" : "pointer",fontSize:13,fontWeight:600}}>
              {loading?(q.saving):`🚚 ${q.shipNow}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Hiyerarşik KK listesi oluşturucu ─────────────────────────────────────────
const buildHierarchicalQC = (records) => {
  // Sıralama zaten filteredQC içinde yapılıyor.
  // Burada tekrar root-first yaparsak yeni ABCDX kayıtları eski demo kayıtlarının altına düşüyor.
  return records.map(r => ({
    ...r,
    _depth: r.parent_order_no ? 1 : 0
  }));
};

export default function Quality() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const q = useMemo(() => l.qualityPage || {}, [l.qualityPage]);
  const [mainTab, setMainTab] = useState(localStorage.getItem("tab_quality") || "quality");
  const [qcRecords, setQcRecords] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewQC, setViewQC] = useState(localStorage.getItem("view_qc") || "list");
  const [viewShip, setViewShip] = useState(localStorage.getItem("view_ship") || "list");
  const [selectedQC, setSelectedQC] = useState(null);
  const [selectedShip, setSelectedShip] = useState(null);
  const [showQCForm, setShowQCForm] = useState(false);
  const [showShipForm, setShowShipForm] = useState(false);
  const [formData, setFormData] = useState(null);
  const [stats, setStats] = useState({});
  const [pageQC, setPageQC] = useState(1);
  const [pageSizeQC, setPageSizeQC] = useState(25);
  const [pageShip, setPageShip] = useState(1);
  const [pageSizeShip, setPageSizeShip] = useState(25);

  const fetchData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [r1,r2] = await Promise.all([fetch(`${API}/quality`,{headers:headers()}),fetch(`${API}/shipments`,{headers:headers()})]);
      if (r1.ok) {
        const data=await r1.json(); const list=Array.isArray(data)?data:data.items||[];
        setQcRecords(list);
        // Yalnızca gerçek kalite kontrol kayıtlarını KPI hesabına al.
        // API'nin eklediği bekleyen iş emri placeholder satırları istatistikleri
        // şişirmemeli.
        const validChecks = list.filter(r => r && r.id);

        const normalizedChecks = validChecks.map(r => {
          const total = Math.max(0, Number(r.total_qty) || 0);
          const rawPassed = Math.max(0, Number(r.pass_qty) || 0);
          const rawFailed = Math.max(0, Number(r.fail_qty) || 0);

          // Bozuk/eski verilerde geçen ve kalan miktarlar toplamı aşamaz.
          const passed = Math.min(total, rawPassed);
          const failed = Math.min(total, rawFailed);

          return {
            total,
            passed,
            failed,
            status: String(r.status || "").toLowerCase()
          };
        });

        const totalQty = normalizedChecks.reduce(
          (sum, r) => sum + r.total,
          0
        );

        const passQty = normalizedChecks.reduce(
          (sum, r) => sum + r.passed,
          0
        );

        const passedCheckCount = normalizedChecks.filter(r =>
          r.status === "passed" ||
          (r.total > 0 && r.passed === r.total && r.failed === 0)
        ).length;

        const failedCheckCount = normalizedChecks.filter(r =>
          ["failed", "rejected"].includes(r.status) ||
          r.failed > 0
        ).length;

        const rawPassRate = totalQty > 0
          ? (passQty / totalQty) * 100
          : 0;

        const safePassRate = Math.min(
          100,
          Math.max(0, rawPassRate)
        );

        setStats(p => ({
          ...p,
          qcTotal: validChecks.length,
          passed: passedCheckCount,
          totalFailed: failedCheckCount,
          avgPass: safePassRate.toFixed(1)
        }));
      } else throw new Error(q.errFetchQC);
      if (r2.ok) {
        const data=await r2.json(); const list=Array.isArray(data)?data:data.items||[];
        setShipments(list);
        setStats(p=>({...p,shipTotal:list.length,shipped:list.filter(s=>s.status==="shipped"||s.status==="delivered").length,pendingShip:list.filter(s=>["draft","ready","pending"].includes(s.status)).length}));
      }
    } catch(e){setError(e.message);} finally{setLoading(false);}
  },[q]);

  useEffect(()=>{fetchData();},[fetchData]);

  const QC_STATUS = getQCStatus();
  const SHIP_STATUS = getShipStatus();

  const filteredQC = qcRecords.filter(r=>{
    const ms=!search||[(r.product_name||""),(r.product_code||""),(r.order_no||""),(r.inspector_name||"")].some(s=>s.toLowerCase().includes(search.toLowerCase()));
    const effectiveStatus = r.work_order_status==='completed' ? 'passed' : r.status;
    return ms&&(filterStatus==="all"||effectiveStatus===filterStatus);
  }).sort((a,b)=>{
    const rootNo = (r) => {
      const no = r.order_no || "";
      const parts = no.split("-");
      return parts.length >= 3 ? parts.slice(0,3).join("-") : no;
    };

    const rootA = rootNo(a);
    const rootB = rootNo(b);

    if (rootA !== rootB) {
      return rootB.localeCompare(rootA, "tr", { numeric: true });
    }

    const aChild = !!a.parent_order_no;
    const bChild = !!b.parent_order_no;

    if (aChild !== bChild) return aChild ? 1 : -1;

    return (a.order_no || "").localeCompare(b.order_no || "", "tr", { numeric: true });
  });

  const filteredShip=shipments.filter(r=>{
    const ms=!search||[(r.customer||r.customer_name||""),(r.product_name||""),(r.order_no||"")].some(s=>s.toLowerCase().includes(search.toLowerCase()));
    return ms&&(filterStatus==="all"||r.status===filterStatus);
  });

  const hierarchicalQC = buildHierarchicalQC(filteredQC);

  const pagedQC = hierarchicalQC.slice((pageQC-1)*pageSizeQC, pageQC*pageSizeQC);
  const totalPagesQC = Math.ceil(hierarchicalQC.length / pageSizeQC);
  const pagedShip = filteredShip.slice((pageShip-1)*pageSizeShip, pageShip*pageSizeShip);
  const totalPagesShip = Math.ceil(filteredShip.length / pageSizeShip);

  const selectStyle={padding:"10px 14px",background:t.bg.input,border:`1px solid ${t.border.input}`,borderRadius:10,color:t.text.primary,fontSize:13,outline:"none",cursor:"pointer"};
  const renderShipPct=(r)=>{const total=r.quantity||0;const shipped=r.shipped_quantity||0;const ready=r.remaining_quantity||0;const pct=r.status==='shipped'?100:total>0?Math.round((shipped+ready)/total*100):0;const color=r.status==='shipped'?t.accent.green:pct===100?t.accent.green:pct>0?"#f97316":t.accent.red;return{total,shipped,ready,pct,color};};
  const currentView=mainTab==="quality"?viewQC:viewShip;

  const renderQCStatusBadge = (r) => {
    const st = r.work_order_status || r.status;
    const isMainWO = !r.parent_order_no;

    if (st === 'final_qc_pending' || (st === 'qc_pending' && isMainWO)) {
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"#8b5cf611",color:"#a78bfa",border:"1px solid #8b5cf633",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#8b5cf6"}}/> Final KK Bekliyor
        </span>
      );
    }

    if (st === 'qc_pending') {
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"#f9731611",color:"#f97316",border:"1px solid #f9731633",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#f97316"}}/> İç Proses KK Bekliyor
        </span>
      );
    }
    if (r.work_order_status === 'completed' && Number(r.quantity_checked || r.total_checked || r.total || 0) > 0) {
      const isFinal =
        !r.parent_order_no &&
        (
          (r.notes && r.notes.toLowerCase().includes("final")) ||
          (r.check_type && r.check_type.toLowerCase().includes("final")) ||
          r.work_order_status === "completed"
        );
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(0,255,157,0.12)",color:"#00ff9d",border:"1px solid rgba(0,255,157,0.3)",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#00ff9d"}}/>
          {isFinal ? q.finalQcPassedLabel : q.inProcessQcPassedLabel}
        </span>
      );
    }
    if (r.status === "passed") {
      const isFinal =
        !r.parent_order_no &&
        (
          (r.notes && r.notes.toLowerCase().includes("final")) ||
          (r.check_type && r.check_type.toLowerCase().includes("final")) ||
          r.work_order_status === "completed"
        );
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(0,255,157,0.12)",color:"#00ff9d",border:"1px solid rgba(0,255,157,0.3)",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#00ff9d"}}/>
          {isFinal ? q.finalQcPassedLabel : q.inProcessQcPassedLabel}
        </span>
      );
    }
    if (!r.id) {
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(6,182,212,0.12)",color:"#06b6d4",border:"1px solid rgba(6,182,212,0.3)",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#06b6d4"}}/> {["completed","final_qc_passed","qc_passed"].includes(r.work_order_status || r.status) ? q.woStatusCompleted : (r.work_order_status || r.status)==="planned" ? q.woStatusPlanned : (r.work_order_status || r.status)==="in_progress" ? q.woStatusInProgress : ["assembly_pending","assembly_in_progress"].includes(r.work_order_status || r.status) ? q.woStatusAssemblyWaiting : (r.work_order_status || r.status)==="qc_pending" ? q.woStatusQcWaiting : (r.work_order_status || r.status)==="final_qc_pending" ? q.woStatusFinalQcWaiting : q.woStatusWaiting}
        </span>
      );
    }
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(6,182,212,0.12)",color:"#06b6d4",border:"1px solid rgba(6,182,212,0.3)",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>
        <span style={{width:6,height:6,borderRadius:"50%",background:"#06b6d4"}}/> Bekliyor
      </span>
    );
  };

  return (
    <div style={{minHeight:"100vh",background:t.bg.page,color:t.text.primary,fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
      <style>{`@keyframes sf-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{maxWidth:1400,margin:"0 auto",padding:"28px 24px"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
          <div>
            <h1 style={{fontSize:26,fontWeight:800,color:t.text.primary,margin:0}}>✅ {q.title}</h1>
            <p style={{color:t.text.muted,margin:"6px 0 0",fontSize:14}}>{q.subtitle}</p>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={fetchData} style={{padding:"10px 16px",borderRadius:10,border:`1px solid ${t.border.input}`,background:t.bg.toggle,color:t.text.secondary,cursor:"pointer",fontSize:13}}>🔄 {q.refresh}</button>
            <button onClick={()=>{
              const isQuality = mainTab === "quality";
              const hdrs = isQuality
                ? [q.orderNo, q.productCode, q.passedQtyLabel, q.failed_, q.statusLabel, q.inspector, q.date_]
                : [q.customer_, q.product_, q.totalOrder, q.shippedQty, q.orderNo, q.date_, q.statusLabel];
              const rows = isQuality
                ? (qcRecords||[]).map(qr => [qr.order_no||"", qr.product_code||"", qr.quantity_passed||0, qr.quantity_failed||0, qr.status||"", qr.checked_by||"", qr.check_date ? new Date(qr.check_date).toLocaleDateString(getLocale()) : ""])
                : (shipments||[]).map(s => [s.customer||s.customer_name||"", s.product_name||s.product_code||"", s.quantity||0, s.shipped_quantity||0, s.order_no||s.sales_order_no||"", s.ship_date ? new Date(s.ship_date).toLocaleDateString(getLocale()) : "", s.status||""]);
              const filename = isQuality ? `quality_control_${new Date().toISOString().slice(0,10)}.csv` : `shipments_${new Date().toISOString().slice(0,10)}.csv`;
              const csv = [hdrs, ...rows].map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(";")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
            }} style={{padding:"10px 16px",borderRadius:10,border:`1px solid ${t.border.input}`,background:t.bg.toggle,color:t.text.secondary,cursor:"pointer",fontSize:13}}>📥 Excel</button>
            <button onClick={()=>{setFormData(null);if(mainTab==="quality")setShowQCForm(true);else setShowShipForm(true);}} style={{padding:"10px 20px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#3b82f6,#6366f1)",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:600}}>
              + {mainTab==="quality"?(q.addQCRecord):(q.addShipment)}
            </button>
          </div>
        </div>

        <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
          {(mainTab==="quality"?[
            {label: q.totalQC,     value:stats.qcTotal||0,   color:t.accent.blue,   icon:"📊"},
            {label: q.passed_,         value:stats.passed||0,    color:t.accent.green,  icon:"✅"},
            {label: q.failed_,     value:stats.totalFailed||0,color:t.accent.red,   icon:"✅"},
            {label: q.avgPass,    value:`${stats.avgPass||0}%`,color:Number(stats.avgPass)>=95?t.accent.green:t.accent.yellow,icon:"📊"},
          ]:[
            {label: q.totalShip,value:stats.shipTotal||0, color:t.accent.blue,  icon:"📊"},
            {label: q.shipped_,   value:stats.shipped||0,   color:t.accent.green,  icon:"✅"},
            {label: q.pending_,      value:stats.pendingShip||0,color:t.accent.yellow, icon:"✅"},
          ]).map(stat=>(
            <div key={stat.label} style={{flex:1,minWidth:140,background:t.bg.card,border:`1px solid ${t.border.card}`,borderRadius:14,padding:"16px 20px",display:"flex",alignItems:"center",gap:14,boxShadow:t.shadow.card}}>
              <span style={{fontSize:24}}>{stat.icon}</span>
              <div><div style={{fontSize:22,fontWeight:800,color:stat.color}}>{stat.value}</div><div style={{fontSize:12,color:t.text.muted,fontWeight:600}}>{stat.label}</div></div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:4,marginBottom:20,background:t.bg.toggle,border:`1px solid ${t.border.input}`,borderRadius:12,padding:4,width:"fit-content"}}>
          {[["quality",`🔍 ${q.tabQC}`],["shipment",`🚚 ${q.tabShipment}`]].map(([v,label])=>(
            <button key={v} onClick={()=>{setMainTab(v);localStorage.setItem("tab_quality",v);setSearch("");setFilterStatus("all");}} style={{padding:"9px 20px",borderRadius:9,border:"none",cursor:"pointer",background:mainTab===v?(themeName==="dark"?"rgba(255,255,255,0.12)":"#fff"):"none",color:mainTab===v?t.text.primary:t.text.muted,fontSize:13,fontWeight:600,boxShadow:mainTab===v?t.shadow.card:"none"}}>{label}</button>
          ))}
        </div>

        <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
          <input placeholder={`🔍 ${mainTab==="quality"?(q.searchQC):(q.searchShip)}`} value={search} onChange={e=>setSearch(e.target.value)} style={{...selectStyle,flex:1,minWidth:220}}/>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="all">{q.allStatuses}</option>
            {Object.entries(mainTab==="quality"?QC_STATUS:SHIP_STATUS).map(([k,v])=>(<option key={k} value={k}>{v.label}</option>))}
          </select>
          <div style={{display:"flex",gap:4,background:t.bg.toggle,border:`1px solid ${t.border.input}`,borderRadius:10,padding:4}}>
            {[["list","☰"],["grid","⊞"]].map(([v,icon])=>(
              <button key={v} onClick={()=>{if(mainTab==="quality"){setViewQC(v);localStorage.setItem("view_qc",v);}else{setViewShip(v);localStorage.setItem("view_ship",v);}}} style={{padding:"6px 12px",borderRadius:7,border:"none",cursor:"pointer",background:currentView===v?t.bg.toggleActive:"none",color:currentView===v?t.text.primary:t.text.muted,fontSize:16}}>{icon}</button>
            ))}
          </div>
        </div>

        {loading?(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"80px 0",gap:12}}>
            <div style={{width:28,height:28,border:`2px solid ${t.border.default}`,borderTopColor:t.accent.blue,borderRadius:"50%",animation:"sf-spin 0.8s linear infinite"}}/>
            <span style={{color:t.text.muted}}>{q.loading}</span>
          </div>
        ):error?(
          <div style={{padding:32,textAlign:"center",background:t.accent.redBg,border:`1px solid ${t.accent.red}44`,borderRadius:14,color:t.accent.red}}>
            ⚠️ {error}<br/>
            <button onClick={fetchData} style={{marginTop:12,padding:"8px 16px",borderRadius:8,border:`1px solid ${t.accent.red}44`,background:"none",color:t.accent.red,cursor:"pointer"}}>{q.retry}</button>
          </div>
        ):mainTab==="quality"?(
          viewQC==="list"?(
            <div style={{background:t.bg.card,border:`1px solid ${t.border.card}`,borderRadius:14,overflow:"hidden",boxShadow:t.shadow.card}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:t.bg.theadRow}}>
                    {["#", q.customer_, q.productName_, q.productCode, q.workOrder, q.inspector, q.total_, q.passedQtyLabel, q.failed_, q.passPct, q.statusLabel].map(h=>(
                      <th key={h} style={{padding:"12px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:t.text.label,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hierarchicalQC.length===0?(
                    <tr><td colSpan={11} style={{textAlign:"center",color:t.text.placeholder,padding:40}}>{qcRecords.length===0?(q.noQCRecords):(q.noMatch)}</td></tr>
                  ):pagedQC.map((r,i)=>{
                    const depth = r._depth || 0;
                    const isRoot = depth === 0;
                    const checkedQty = Number(r.total_qty || 0);
                    const passedQty = Number(r.pass_qty || 0);
                    const hasQualityData = checkedQty > 0;
                    const pct = hasQualityData
                      ? Math.min(100, Math.max(0, (passedQty / checkedQty) * 100)).toFixed(1)
                      : null;
                    const pctColor = pct>=95?t.accent.green:pct>=80?t.accent.yellow:t.accent.red;
                    const rowBg = isRoot
                      ? (themeName==="dark" ? "rgba(16,185,129,0.07)" : "rgba(16,185,129,0.04)")
                      : (i%2===0 ? t.bg.rowAlt : t.bg.row);
                    const rowBgHover = isRoot
                      ? (themeName==="dark" ? "rgba(16,185,129,0.13)" : "rgba(16,185,129,0.09)")
                      : t.accent.blueBg;
                    const leftBorder = isRoot
                      ? `3px solid ${t.accent.green}`
                      : depth===1 ? `3px solid ${t.accent.blue}44` : `3px solid transparent`;

                    return (
                      <tr
                        key={r.id ? `qc-${r.id}` : `placeholder-${r.order_no}`}
                        onClick={()=>r.id && setSelectedQC(r)}
                        style={{borderTop:`1px solid ${t.border.subtle}`,background:rowBg,cursor:r.id?"pointer":"default",borderLeft:leftBorder}}
                        onMouseEnter={e=>e.currentTarget.style.background=rowBgHover}
                        onMouseLeave={e=>e.currentTarget.style.background=rowBg}
                      >
                        <td style={{padding:"12px 14px",fontWeight:700,color:t.accent.blue,fontSize:13}}>
                          {r.id ? `#${r.id}` : <span style={{color:t.text.muted,fontSize:11}}>—</span>}
                        </td>
                        <td style={{padding:"12px 14px",fontSize:12,color:t.text.secondary}}>{r.customer||""}</td>
                        <td style={{padding:"12px 14px",fontWeight:isRoot?800:600,color:isRoot?t.accent.green:t.text.primary,fontSize:13}}>
                          {r.product_name||r.product_code||""}
                        </td>
                        <td style={{padding:"12px 14px",fontSize:12,color:t.text.muted}}>{r.product_code||""}</td>
                        <td style={{paddingTop:"12px",paddingBottom:"12px",paddingRight:"14px",paddingLeft:`${14 + depth * 20}px`,fontSize:12}}>
                          {depth > 0 && <span style={{color:t.text.muted,marginRight:4,fontSize:11}}>└─</span>}
                          <span style={{color:isRoot?t.accent.green:t.accent.blue,fontWeight:isRoot?800:600,fontSize:isRoot?13:12}}>
                            {r.order_no||""}
                          </span>
                        </td>
                        <td style={{padding:"12px 14px",fontSize:12,color:t.text.muted}}>{r.inspector_name||""}</td>
                        <td style={{padding:"12px 14px",fontSize:13}}>
                          <div style={{fontWeight:700,color:t.text.primary}}>{r.sales_order_qty||r.wo_quantity||r.total_qty||0}</div>
                          <div style={{fontSize:11,color:t.text.muted}}>{q.checked_}: {r.total_qty||0}</div>
                        </td>
                        <td style={{padding:"12px 14px",fontWeight:700,color:t.accent.green}}>{r.pass_qty||0}</td>
                        <td style={{padding:"12px 14px",fontWeight:700,color:t.accent.red}}>{r.fail_qty||0}</td>
                        <td style={{padding:"12px 14px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{flex:1,height:5,background:t.bg.tag,borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct === null ? 0 : pct}%`,background:pctColor,borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:11,fontWeight:700,color:pct === null ? t.text.muted : pctColor,flexShrink:0}}>
                              {pct === null ? "—" : `${pct}%`}
                            </span>
                          </div>
                        </td>
                        <td style={{padding:"12px 14px"}}>
                          {renderQCStatusBadge(r)}
                          {r.work_order_status && ['assembly_pending','assembly_in_progress','assembly_done'].includes(r.work_order_status) && (
                            <div style={{marginTop:4,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,
                              background:r.work_order_status==='assembly_in_progress'?"rgba(59,130,246,0.15)":"rgba(245,158,11,0.15)",
                              color:r.work_order_status==='assembly_in_progress'?"#3b82f6":"#f59e0b",
                              display:"inline-block"}}>
                              {r.work_order_status==='assembly_in_progress' ? q.assemblyInProgress : r.work_order_status==='assembly_pending' ? q.assemblyPending : q.assemblyCompleted}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
              {filteredQC.length===0?(
                <div style={{gridColumn:"1/-1",textAlign:"center",color:t.text.placeholder,padding:"60px 0"}}>{qcRecords.length===0?(q.noQCRecords):(q.noMatch)}</div>
              ):filteredQC.filter(r=>r.id).map((r,idx)=>{
                const checkedQty = Number(r.total_qty || 0);
                const passedQty = Number(r.pass_qty || 0);
                const pct = checkedQty > 0
                  ? Math.min(100, Math.max(0, (passedQty / checkedQty) * 100)).toFixed(1)
                  : null;
                const pctColor = pct === null
                  ? t.text.muted
                  : Number(pct) >= 95
                    ? t.accent.green
                    : Number(pct) >= 80
                      ? t.accent.yellow
                      : t.accent.red;
                const c=scQC(r.status,themeName);
                return(
                  <div key={r.id||idx} onClick={()=>setSelectedQC(r)} style={{background:t.bg.card,border:`1px solid ${t.border.card}`,borderRadius:14,padding:18,cursor:"pointer",transition:"all 0.2s",boxShadow:t.shadow.card,position:"relative",overflow:"hidden"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=`${c.color}55`;}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.borderColor=t.border.card;}}>
                    <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${c.color},transparent)`,borderRadius:"14px 14px 0 0"}}/>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:t.text.primary}}>{r.product_name||r.product_code||""}</div>
                        <div style={{fontSize:11,color:t.text.muted}}>{q.woShort}: {r.order_no||""} · {r.customer||""}</div>
                      </div>
                      <QCBadge status={r.status} themeName={themeName}/>
                    </div>
                    <div style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:11,color:t.text.muted}}>{q.passRate}</span><span style={{fontSize:12,fontWeight:700,color:pctColor}}>{pct === null ? "—" : `${pct}%`}</span></div>
                      <div style={{height:6,background:t.bg.tag,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${pct === null ? 0 : pct}%`,background:pctColor,borderRadius:3}}/></div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                      {[[q.total_,r.total_qty||0,t.text.secondary],[q.passedQtyLabel,r.pass_qty||0,t.accent.green],[q.failed_,r.fail_qty||0,t.accent.red]].map(([lbl,v,col])=>(
                        <div key={lbl} style={{background:t.bg.metricCard,borderRadius:7,padding:"7px 8px",border:`1px solid ${t.border.subtle}`,textAlign:"center"}}>
                          <div style={{fontSize:9,color:t.text.muted,textTransform:"uppercase"}}>{lbl}</div>
                          <div style={{fontSize:14,fontWeight:800,color:col}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ):(
          viewShip==="list"?(
            <div style={{background:t.bg.card,border:`1px solid ${t.border.card}`,borderRadius:14,overflow:"hidden",boxShadow:t.shadow.card}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:t.bg.theadRow}}>
                  {["#", q.customer_, q.product_, q.totalOrder, q.shippedQty, q.orderNo, q.shipDate, q.statusLabel].map(h=>(<th key={h} style={{padding:"12px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:t.text.label,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {filteredShip.length===0?(
                    <tr><td colSpan={8} style={{textAlign:"center",color:t.text.placeholder,padding:40}}>{shipments.length===0?(q.noShipments):(q.noMatch)}</td></tr>
                  ):pagedShip.map((r,i)=>{
                    const {total,shipped,ready,pct,color}=renderShipPct(r);
                    return(
                      <tr key={r.id} onClick={()=>setSelectedShip(r)} style={{borderTop:`1px solid ${t.border.subtle}`,background:i%2===0?t.bg.rowAlt:t.bg.row,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=t.accent.blueBg} onMouseLeave={e=>e.currentTarget.style.background=i%2===0?t.bg.rowAlt:t.bg.row}>
                        <td style={{padding:"12px 14px",fontWeight:700,color:t.accent.blue,fontSize:13}}>#{r.id}</td>
                        <td style={{padding:"12px 14px",fontWeight:600,color:t.text.primary,fontSize:13}}>{r.customer||r.customer_name||""}</td>
                        <td style={{padding:"12px 14px",fontSize:13,color:t.text.secondary}}>{r.product_name||r.product_code||""}</td>
                        <td style={{padding:"12px 14px",fontSize:13,color:t.text.secondary}}>{total} {r.unit||q.pcs}</td>
                        <td style={{padding:"12px 14px",fontSize:13,fontWeight:700}}>
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            <span style={{color,fontWeight:700}}>
                              {r.status==="cancelled"?"":shipped>0?`${shipped}`:ready>0?`${ready} ${q.readyToShip}`:"0"} {r.status!=="cancelled"&&(r.unit||q.pcs)}
                            </span>
                            <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:60,height:4,background:t.bg.tag,borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:2}}/></div><span style={{fontSize:10,color,fontWeight:700}}>%{pct}</span></div>
                          </div>
                        </td>
                        <td style={{padding:"12px 14px",fontSize:12,color:t.text.muted}}>{r.order_no||""}</td>
                        <td style={{padding:"12px 14px",fontSize:12,color:t.text.muted}}>{fmt(r.ship_date)}</td>
                        <td style={{padding:"12px 14px"}}>
                          <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start"}}>
                            <ShipBadge status={r.status} themeName={themeName}/>
                            {Number(r.invoiced_quantity || 0) > 0 && (
                              <div style={{fontSize:10,fontWeight:700,color:"#10b981",whiteSpace:"nowrap"}}>
                                🧾 Fatura Kesildi
                              </div>
                            )}
                          </div>
                          {r.status==="cancelled"&&(<div style={{fontSize:10,color:"#06b6d4",marginTop:3,fontWeight:600}}>⚠️ {q.customerCancel}</div>)}
                          {!r.skip_invoice&&r.source_type!=='rework'&&(r.invoiced_quantity||0)<=(r.shipped_quantity||0)&&r.status==="pending"&&(<div style={{fontSize:10,color:"#f97316",marginTop:3,fontWeight:600}}>🧾 {q.notInvoiced}</div>)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
              {filteredShip.length===0?(
                <div style={{gridColumn:"1/-1",textAlign:"center",color:t.text.placeholder,padding:"60px 0"}}>{shipments.length===0?(q.noShipments):(q.noMatch)}</div>
              ):pagedShip.map((r,idx)=>{
                const{total,shipped,pct,color}=renderShipPct(r);
                const sc=scShip(r.status,themeName);
                return(
                  <div key={r.id||idx} onClick={()=>setSelectedShip(r)} style={{background:t.bg.card,border:`1px solid ${r.status==="cancelled"?"rgba(6,182,212,0.3)":t.border.card}`,borderRadius:14,padding:18,cursor:"pointer",transition:"all 0.2s",boxShadow:t.shadow.card,position:"relative",overflow:"hidden"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=`${sc.color}55`;}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.borderColor=r.status==="cancelled"?"rgba(6,182,212,0.3)":t.border.card;}}>
                    <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${color},transparent)`,borderRadius:"14px 14px 0 0"}}/>
                    {!r.skip_invoice&&r.source_type!=='rework'&&(r.invoiced_quantity||0)<=(r.shipped_quantity||0)&&r.status==="pending"&&(
                      <div style={{fontSize:11,color:"#f97316",background:"#f9731611",border:"1px solid #f9731633",borderRadius:6,padding:"4px 8px",marginBottom:8,fontWeight:600}}>
                        ⚠️ {q.invoiceRequiredCard}
                      </div>
                    )}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:t.text.primary,marginBottom:3}}>{r.customer||r.customer_name||""}</div>
                        <div style={{fontSize:11,color:t.text.muted}}>{r.product_name||r.product_code||""}</div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
                        <ShipBadge status={r.status} themeName={themeName}/>
                        {r.status==="cancelled"&&(<div style={{fontSize:10,color:"#06b6d4",fontWeight:600,whiteSpace:"nowrap"}}>⚠️ {q.customerCancel}</div>)}
                      </div>
                    </div>
                    <div style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:11,color:t.text.muted}}>{q.shipProgress}</span><span style={{fontSize:12,fontWeight:700,color}}>{pct}%</span></div>
                      <div style={{height:6,background:t.bg.tag,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:3}}/></div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {[
                        [q.order_,     `${total} ${r.unit||q.pcs}`, t.text.secondary],
                        [q.shippedOf,`${shipped} ${r.unit||q.pcs}`, color],
                        [q.remaining_,    `${total-shipped} ${r.unit||q.pcs}`, total-shipped>0?t.accent.red:t.accent.green],
                        [q.orderNo,  r.order_no||"", t.text.muted],
                      ].map(([label,value,col])=>(
                        <div key={label} style={{background:t.bg.metricCard,borderRadius:7,padding:"7px 8px",border:`1px solid ${t.border.subtle}`}}>
                          <div style={{fontSize:9,color:t.text.muted,textTransform:"uppercase",marginBottom:2}}>{label}</div>
                          <div style={{fontSize:12,fontWeight:700,color:col}}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:10,fontSize:11,color:t.text.muted}}>📅 {fmt(r.ship_date)}</div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {!loading&&!error&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:16,flexWrap:"wrap",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{color:t.text.muted,fontSize:12}}>Sayfa başı:</span>
              {[25,50,100].map(n=>(
                <button key={n} onClick={()=>{if(mainTab==="quality"){setPageSizeQC(n);setPageQC(1);}else{setPageSizeShip(n);setPageShip(1);}}} style={{padding:"4px 12px",borderRadius:8,border:`1px solid ${t.border.input}`,background:(mainTab==="quality"?pageSizeQC:pageSizeShip)===n?"#10b981":"transparent",color:(mainTab==="quality"?pageSizeQC:pageSizeShip)===n?"#fff":t.text.muted,cursor:"pointer",fontSize:12,fontWeight:600}}>{n}</button>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <button onClick={()=>mainTab==="quality"?setPageQC(1):setPageShip(1)} disabled={(mainTab==="quality"?pageQC:pageShip)===1} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${t.border.input}`,background:"transparent",color:t.text.muted,cursor:"pointer",fontSize:12}}>«</button>
              <button onClick={()=>mainTab==="quality"?setPageQC(p=>Math.max(1,p-1)):setPageShip(p=>Math.max(1,p-1))} disabled={(mainTab==="quality"?pageQC:pageShip)===1} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${t.border.input}`,background:"transparent",color:t.text.primary,cursor:"pointer",fontSize:14}}>‹</button>
              {Array.from({length:Math.min(5,mainTab==="quality"?totalPagesQC:totalPagesShip||1)},(_,i)=>{
                const total=mainTab==="quality"?totalPagesQC:totalPagesShip||1;
                const cur=mainTab==="quality"?pageQC:pageShip;
                let page=i+1;
                if(total>5){if(cur<=3)page=i+1;else if(cur>=total-2)page=total-4+i;else page=cur-2+i;}
                return(
                  <button key={page} onClick={()=>mainTab==="quality"?setPageQC(page):setPageShip(page)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${page===(mainTab==="quality"?pageQC:pageShip)?"#10b981":t.border.input}`,background:page===(mainTab==="quality"?pageQC:pageShip)?"#10b981":"transparent",color:page===(mainTab==="quality"?pageQC:pageShip)?"#fff":t.text.primary,cursor:"pointer",fontSize:12,fontWeight:600,minWidth:32}}>{page}</button>
                );
              })}
              <button onClick={()=>mainTab==="quality"?setPageQC(p=>Math.min(totalPagesQC,p+1)):setPageShip(p=>Math.min(totalPagesShip,p+1))} disabled={(mainTab==="quality"?pageQC:pageShip)>=(mainTab==="quality"?totalPagesQC:totalPagesShip||1)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${t.border.input}`,background:"transparent",color:t.text.primary,cursor:"pointer",fontSize:14}}>›</button>
              <button onClick={()=>mainTab==="quality"?setPageQC(totalPagesQC):setPageShip(totalPagesShip)} disabled={(mainTab==="quality"?pageQC:pageShip)>=(mainTab==="quality"?totalPagesQC:totalPagesShip||1)} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${t.border.input}`,background:"transparent",color:t.text.muted,cursor:"pointer",fontSize:12}}>»</button>
            </div>
            <span style={{color:t.text.muted,fontSize:12}}>
              {mainTab==="quality"?`${filteredQC.length} kayıt`:`${filteredShip.length} sevkiyat`}
            </span>
          </div>
        )}
      </div>

      {selectedQC&&<QCDetailModal record={selectedQC} t={t} themeName={themeName} onClose={()=>setSelectedQC(null)} onCancelled={()=>{fetchData();setSelectedQC(null);}}/>}
      {selectedShip&&<ShipmentDetailModal record={selectedShip} t={t} themeName={themeName} onClose={()=>setSelectedShip(null)} onStatusChange={()=>{fetchData();setSelectedShip(null);}}/>}
      {showQCForm&&<QCFormModal t={t} onClose={()=>setShowQCForm(false)} onSaved={()=>{setShowQCForm(false);fetchData();}}/>}
      {showShipForm&&<ShipFormModal t={t} data={formData} onClose={()=>{setShowShipForm(false);setFormData(null);}} onSaved={()=>{setShowShipForm(false);setFormData(null);fetchData();}}/>}
    </div>
  );
}